// src/components/fnct/GestesAction.tsx — SCE Issues
// Les gestes sur une action FNC-T, option B de la maquette du 24/10
// (https://claude.ai/artifact/Ep191WJApDfqQsvJqewffq) : des boutons ronds de
// 38 px, l'icône dedans, le mot en tout petit dessous — Clore (coche verte),
// Reporter (calendrier) et, depuis le 30/10, Confier (flèche : à quelqu'un
// d'autre, motif obligatoire). Compacts, de même forme, lisibles au téléphone
// sans survol ; la cible fait 52 × 54 px. Le geste dont le formulaire est ouvert
// sous la rangée reste enfoncé (contour noir). Reporter ne se grise plus : une
// action se reporte autant de fois qu'il le faut (30/10). Styles : `.geste-rond`.
// Partout où l'on agit sur une action : Mes FNC-T, l'accueil, le plan d'actions
// de la fiche (26/10, avec un 3e rond « Modifier » pour qui gère les actions, à la
// place de l'ancien menu « ⋯ »).
//
// Aussi ici : la pastille d'échéance, d'un coup d'œil (« 5 j de retard »,
// « Demain », « 13/11 · 20 j »), commune aux mêmes listes.

import type { JSX } from 'react'
import { AlertCircle, CalendarClock, CheckCircle2, Clock, Forward, PenLine } from 'lucide-react'
import { joursEntre } from '@/utils/fnct'
import type { Jour } from '@/types/fnct.types'
import type { GesteAction } from './GesteActionEnPlace'

const jj = (j: string): string => `${j.slice(8, 10)}/${j.slice(5, 7)}`

/** Un geste en rond (38 px, mot dessous) ; aussi « Clore » d'une FNC-T dans la revue (02/11). */
export function BoutonRond({
  libelle,
  icone,
  ouvert,
  disabled,
  titre,
  onClick,
}: {
  libelle: string
  icone: JSX.Element
  ouvert?: boolean
  disabled?: boolean
  titre: string
  onClick: () => void
}): JSX.Element {
  return (
    <button type="button" className="geste-rond" onClick={onClick} disabled={disabled} aria-expanded={ouvert} title={titre}>
      <span className="geste-rond-cercle">{icone}</span>
      <span className="geste-rond-libelle">{libelle}</span>
    </button>
  )
}

/** Clore, Reporter et Confier, côte à côte ; Modifier en 4e si `onModifier`. */
export function GestesAction({
  ouvert,
  onGeste,
  onModifier,
  modifierOuvert = false,
}: {
  /** Le geste dont le formulaire est ouvert sous la rangée. */
  ouvert: GesteAction | null
  onGeste: (geste: GesteAction) => void
  /** Qui gère les actions (fiche FNC-T) : description, responsable, catégorie, échéance. */
  onModifier?: () => void
  modifierOuvert?: boolean
}): JSX.Element {
  return (
    <span className="flex" style={{ gap: 2, flexShrink: 0 }}>
      <BoutonRond
        libelle="Clore"
        icone={<CheckCircle2 size={18} color="var(--color-traffic-green)" aria-hidden="true" />}
        ouvert={ouvert === 'clore'}
        titre="Clore l’action"
        onClick={() => onGeste('clore')}
      />
      <BoutonRond
        libelle="Reporter"
        icone={<CalendarClock size={18} aria-hidden="true" />}
        ouvert={ouvert === 'reporter'}
        titre="Reporter l’échéance, avec un motif"
        onClick={() => onGeste('reporter')}
      />
      <BoutonRond
        libelle="Confier"
        icone={<Forward size={18} aria-hidden="true" />}
        ouvert={ouvert === 'confier'}
        titre="Confier l’action à quelqu’un d’autre, avec un motif"
        onClick={() => onGeste('confier')}
      />
      {onModifier && (
        <BoutonRond
          libelle="Modifier"
          icone={<PenLine size={17} aria-hidden="true" />}
          ouvert={modifierOuvert}
          titre="Modifier l’action : description, responsable, catégorie, échéance"
          onClick={onModifier}
        />
      )}
    </span>
  )
}

/** Une pastille : icône + quelques mots, rouge pour un retard, verte pour « à temps ». `petite` : sous le titre d'une rangée. */
export function Pastille({ ton, icone, texte, titre, petite = false }: { ton: 'rouge' | 'vert' | 'neutre'; icone: JSX.Element; texte: string; titre?: string; petite?: boolean }): JSX.Element {
  const couleur = ton === 'rouge' ? 'var(--color-traffic-red)' : ton === 'vert' ? 'var(--color-traffic-green)' : 'var(--color-black)'
  return (
    <span
      title={titre}
      className="inline-flex items-center"
      style={{
        gap: 5,
        height: petite ? 24 : 28,
        padding: petite ? '0 8px' : '0 10px',
        borderRadius: petite ? 12 : 14,
        border: `1px solid ${ton === 'neutre' ? 'var(--color-gray-20)' : couleur}`,
        color: couleur,
        fontSize: petite ? 12 : 13,
        fontWeight: ton === 'neutre' ? 500 : 600,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
        backgroundColor: 'var(--color-white)',
      }}
    >
      {icone}
      {texte}
    </span>
  )
}

/** L'échéance d'une action ouverte : « 5 j de retard » (rouge), « Aujourd’hui », « Demain », « 13/11 · 20 j » ; calendrier si reportée. */
export function PastilleEcheance({
  echeance,
  retard,
  reportee,
  aujourdhui,
  petite,
}: {
  /** L'échéance en vigueur (la reportée si elle existe). */
  echeance: Jour | null
  /** Jours de retard ; 0 si l'action n'est pas en retard. */
  retard: number
  reportee: boolean
  aujourdhui: Jour
  petite?: boolean
}): JSX.Element {
  const titre = [echeance ? `Échéance le ${jj(echeance)}` : 'Sans échéance', reportee ? 'reportée' : ''].filter(Boolean).join(', ')
  if (retard > 0) return <Pastille petite={petite} ton="rouge" icone={<AlertCircle size={14} aria-hidden="true" />} texte={`${retard} j de retard`} titre={titre} />
  const icone = reportee ? <CalendarClock size={14} aria-hidden="true" /> : <Clock size={14} aria-hidden="true" />
  if (!echeance) return <Pastille petite={petite} ton="neutre" icone={icone} texte="Sans échéance" />
  const n = joursEntre(aujourdhui, echeance)
  return <Pastille petite={petite} ton="neutre" icone={icone} texte={n === 0 ? 'Aujourd’hui' : n === 1 ? 'Demain' : `${jj(echeance)} · ${n} j`} titre={titre} />
}
